import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Col, DatePicker, Empty, notification, Row, Select, Spin, Tree, Typography } from 'antd';
import { CalendarOutlined, SaveOutlined, SettingOutlined, UsergroupAddOutlined } from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import { allowed_levels_in_microcredentials } from '../../../constants';
import '../../../components/assessment/AssessmentAssignPanel.css';

const { Text } = Typography;
const { Option } = Select;
const DISPLAY_DATE_FORMAT = 'MMM D, YYYY h:mm a';
const SUBMISSION_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';

function isAllowedMicrocredentialLevel(levelValue, levelName) {
   const normalizedValue = String(levelValue ?? '').trim();
   const normalizedName = String(levelName ?? '').toLowerCase();

   return allowed_levels_in_microcredentials.some(
      (level) => normalizedValue === level || normalizedName.includes(level)
   );
}

export default function PerformanceTaskAssignTool(props) {
   const { manualSaveSlotId, options, parentCallback, saveCallback } = props;
   const assignmentOptions = useMemo(() => options ?? {}, [options]);

   const [expandedKeys, setExpandedKeys] = useState([]);
   const [checkedKeys, setCheckedKeys] = useState([]);
   const [selectedKeys, setSelectedKeys] = useState([]);
   const [treeData, setTreeData] = useState([]);
   const [studentListLoading, setStudentListLoading] = useState(false);
   const [levelOptions, setLevelOptions] = useState([]);
   const [levelsLoading, setLevelsLoading] = useState(false);
   const [manualSaveSlotReady, setManualSaveSlotReady] = useState(false);
   const loadedAssignmentIdRef = useRef(null);

   const hasSelectedLevel = assignmentOptions.level !== undefined && assignmentOptions.level !== null && assignmentOptions.level !== '';
   const selectedStudentCount = checkedKeys.filter((key) => String(key).includes('_') || String(key).includes('|')).length;

   const updateOptions = useCallback((patch) => {
      parentCallback({ ...assignmentOptions, ...patch });
   }, [assignmentOptions, parentCallback]);

   const fetchStudentTree = useCallback(async (level, preselectedStudents = '') => {
      if (level === undefined || level === null || level === '') {
         setTreeData([]);
         setCheckedKeys([]);
         setExpandedKeys([]);
         return;
      }

      try {
         setStudentListLoading(true);
         const response = await axios.get(`${process.env.REACT_APP_API_ASSESSMENT}/studentlistperleveljson?level=${level}`);
         const data = Array.isArray(response.data) ? response.data : [];

         setTreeData(data);
         setCheckedKeys(
            String(preselectedStudents || '')
               .split(',')
               .map((item) => item.trim())
               .filter(Boolean)
         );
      } catch (error) {
         setTreeData([]);
         setCheckedKeys([]);
      } finally {
         setStudentListLoading(false);
      }
   }, []);

   useEffect(() => {
      if (manualSaveSlotId) {
         setManualSaveSlotReady(true);
      }
   }, [manualSaveSlotId]);

   useEffect(() => {
      const loadLevels = async () => {
         try {
            setLevelsLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_API_LOOKUP}/levellist`);
            const data = Array.isArray(response.data) ? response.data : [];
            const filteredLevels = data
               .map((item) => ({ value: item.value, name: item.name }))
               .filter((item) => isAllowedMicrocredentialLevel(item.value, item.name));
            setLevelOptions(filteredLevels);
         } catch (error) {
            setLevelOptions([]);
         } finally {
            setLevelsLoading(false);
         }
      };

      loadLevels();
   }, []);

   useEffect(() => {
      if (!assignmentOptions.id) {
         loadedAssignmentIdRef.current = null;
         return;
      }

      if (assignmentOptions.level !== undefined && assignmentOptions.level !== null && assignmentOptions.level !== '' && loadedAssignmentIdRef.current !== assignmentOptions.id) {
         loadedAssignmentIdRef.current = assignmentOptions.id;
         setExpandedKeys([]);
         fetchStudentTree(assignmentOptions.level, assignmentOptions.assigned_students);
      }
   }, [assignmentOptions.assigned_students, assignmentOptions.id, assignmentOptions.level, fetchStudentTree]);

   const onExpand = (expandedKeysValue) => {
      setExpandedKeys(expandedKeysValue);
   };

   const onSelect = (selectedKeysValue) => {
      setSelectedKeys(selectedKeysValue);
   };

   const onCheck = (checkedKeysValue) => {
      setCheckedKeys(checkedKeysValue);

      const selectedStudents = checkedKeysValue.filter((value) => String(value).includes('_') || String(value).includes('|'));

      updateOptions({
         assigned_students: selectedStudents.join(','),
      });
   };

   const handleLevelChange = async (value) => {
      const nextLevel = value ?? '';

      setTreeData([]);
      setCheckedKeys([]);
      setExpandedKeys([]);
      setSelectedKeys([]);

      updateOptions({
         level: nextLevel,
         assigned_students: '',
      });

      if (!nextLevel) {
         return;
      }

      await fetchStudentTree(nextLevel, '');
   };

   const handleSubmissionDateChange = (value) => {
      updateOptions({
         submission_date: value ? value.format(SUBMISSION_DATE_FORMAT) : '',
      });
   };

   const handleSave = () => {
      if (!assignmentOptions.level) {
         notification.error({
            message: 'Performance Task Assignment',
            description: 'Please select a grade level.',
         });
         return;
      }

      if (!assignmentOptions.assigned_students) {
         notification.error({
            message: 'Performance Task Assignment',
            description: 'Please select at least one student.',
         });
         return;
      }

      if (!assignmentOptions.submission_date) {
         notification.error({
            message: 'Performance Task Assignment',
            description: 'Please enter a submission date.',
         });
         return;
      }

      saveCallback(assignmentOptions);
   };

   const renderManualSaveRow = () => (
      <Row className="assessment-assign-drawer-save-row" style={{ paddingBottom: 12 }}>
         <Col span={24} className="gutter-row">
            <Button className="button-shadow assessment-assign-drawer-save-button" type="primary" block shape="round" onClick={handleSave}>
               <Text ellipsis style={{ color: '#fff' }}><SaveOutlined />&nbsp;Save</Text>
            </Button>
         </Col>
      </Row>
   );

   const selectedSubmissionDate = useMemo(() => {
      if (!assignmentOptions.submission_date) return null;
      const parsed = moment(assignmentOptions.submission_date);
      return parsed.isValid() ? parsed : null;
   }, [assignmentOptions.submission_date]);

   return (
      <div className="assessment-assign-panel">
         <section className="assessment-assign-section">
            <div className="assessment-assign-section-header">
               <span className="assessment-assign-section-icon"><SettingOutlined /></span>
               <div>
                  <Text className="assessment-assign-kicker">Options</Text>
                  <h3>Assignment Settings</h3>
               </div>
            </div>

            <div className="assessment-assign-options-grid">
               <label className="assessment-assign-field is-wide">
                  <span><CalendarOutlined /> Submission Date</span>
                  <DatePicker
                     showTime={{ format: 'hh:mm A', use12Hours: true }}
                     format={(value) => value.format(DISPLAY_DATE_FORMAT)}
                     style={{ width: '100%' }}
                     value={selectedSubmissionDate}
                     onChange={handleSubmissionDateChange}
                     disabledDate={(current) => current && current < moment().startOf('day')}
                  />
               </label>
            </div>
         </section>

         <section className="assessment-assign-section">
            <div className="assessment-assign-section-header">
               <span className="assessment-assign-section-icon"><UsergroupAddOutlined /></span>
               <div>
                  <Text className="assessment-assign-kicker">Recipients</Text>
                  <h3>Assign Students</h3>
               </div>
               <span className="assessment-assign-count">{selectedStudentCount}</span>
            </div>

            <div className="assessment-assign-recipient-card">
               <Select
                  showArrow
                  size="default"
                  className="assessment-assign-level-select"
                  allowClear
                  loading={levelsLoading}
                  placeholder="Select Grade Level"
                  onChange={handleLevelChange}
                  value={assignmentOptions.level || undefined}
               >
                  {levelOptions.map(({ value, name }) => (
                     <Option key={value} value={value}>{name}</Option>
                  ))}
               </Select>

               {studentListLoading ? (
                  <div className="assessment-assign-empty-state"><Spin /></div>
               ) : hasSelectedLevel && treeData.length > 0 ? (
                  <div className="assessment-assign-tree-shell">
                     <Tree
                        checkable
                        onExpand={onExpand}
                        expandedKeys={expandedKeys}
                        autoExpandParent
                        onCheck={onCheck}
                        checkedKeys={checkedKeys}
                        onSelect={onSelect}
                        selectedKeys={selectedKeys}
                        treeData={treeData}
                        defaultExpandParent
                     />
                  </div>
               ) : hasSelectedLevel ? (
                  <div className="assessment-assign-empty-state">
                     <Empty image="../images/brainee_mascot.png" description="No students found" />
                  </div>
               ) : null}

               {manualSaveSlotId && manualSaveSlotReady && document.getElementById(manualSaveSlotId)
                  ? createPortal(renderManualSaveRow(), document.getElementById(manualSaveSlotId))
                  : (!manualSaveSlotId ? renderManualSaveRow() : null)}
            </div>
         </section>
      </div>
   );
}



